import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  Chip, 
  Divider, 
  IconButton, 
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  ListItemSecondaryAction
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BusinessIcon from '@mui/icons-material/Business';
import WorkIcon from '@mui/icons-material/Work';
import ArticleIcon from '@mui/icons-material/Article';
import Person2Icon from '@mui/icons-material/Person2';
import { EmployeeAvatar } from '../../../common/EmployeeAvatar';

const ExpandMore = styled((props) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme, expand }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

const EmployeeCard = ({ employee }) => {
  const [expanded, setExpanded] = useState(false);
  const [expandedMatrices, setExpandedMatrices] = useState({});

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const handleMatrixExpandClick = (matrixId) => {
    setExpandedMatrices(prev => ({
      ...prev,
      [matrixId]: !prev[matrixId]
    }));
  };

  // Function to render template information
  const renderTemplates = () => {
    if (!employee.matchingTemplates || employee.matchingTemplates.length === 0) {
      return (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          No matching templates found
        </Typography>
      );
    }

    // Group templates by matrix
    const templatesByMatrix = employee.matchingTemplates.reduce((acc, template) => {
      const matrixId = template.skillsMatrixId || template.matrixId || 'unknown';
      if (!acc[matrixId]) {
        acc[matrixId] = {
          name: template.skillsMatrixName || template.matrixName || 'Unknown Matrix',
          templates: []
        };
      }
      acc[matrixId].templates.push(template);
      return acc;
    }, {});

    return (
      <List dense sx={{ width: '100%', pt: 0 }}>
        {Object.entries(templatesByMatrix).map(([matrixId, matrix]) => (
          <React.Fragment key={matrixId}>
            <ListItemButton 
              onClick={() => handleMatrixExpandClick(matrixId)}
              sx={{ px: 0 }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <ArticleIcon color="primary" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={matrix.name}
                primaryTypographyProps={{ variant: 'subtitle2' }}
              />
              <ListItemSecondaryAction>
                <ExpandMore
                  expand={expandedMatrices[matrixId] || false}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMatrixExpandClick(matrixId);
                  }}
                  aria-expanded={expandedMatrices[matrixId] || false}
                  aria-label="show matrix versions"
                  size="small"
                >
                  <ExpandMoreIcon fontSize="small" />
                </ExpandMore>
              </ListItemSecondaryAction>
            </ListItemButton>
            <Collapse in={expandedMatrices[matrixId] || false} timeout="auto" unmountOnExit>
              <List component="div" disablePadding dense>
                {matrix.templates.map((template) => (
                  <ListItem key={template.id} sx={{ pl: 4, pr: 0 }}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2">
                            {template.name} {template.version ? `v.${template.version}` : ''}
                          </Typography>
                          {template.weight !== undefined && template.weight !== null && (
                            <Chip
                              label={`Weight: ${template.weight}%`}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ height: '20px', fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                      }
                      secondary={`Position: ${template.position} | Roles: ${template.roles}`}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </React.Fragment>
        ))}
      </List>
    );
  };

  return (
    <Card 
      elevation={3} 
      sx={{ 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderLeft: employee.hasMatchingTemplate ? '4px solid #4CAF50' : '4px solid #F44336'
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <EmployeeAvatar 
            employee={employee} 
            size={40}
            sx={{ 
              mr: 2,
              bgcolor: employee.hasMatchingTemplate ? '#4CAF50' : '#F44336'
            }}
          />
          <Box>
            <Typography variant="h6" component="div">
              {employee.fullName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {employee.email}
            </Typography>
          </Box>
          <Box sx={{ ml: 'auto' }}>
            {employee.hasMatchingTemplate ? (
              <CheckCircleIcon color="success" />
            ) : (
              <CancelIcon color="error" />
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Person2Icon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
          <Typography variant="body2">
            Position: {employee.position}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <WorkIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
          <Typography variant="body2">
            Assignments: {employee.assignmentsCount}
          </Typography>
        </Box>

        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Chip 
            label={employee.hasMatchingTemplate ? "Has matching template" : "No matching template"} 
            size="small"
            color={employee.hasMatchingTemplate ? "success" : "error"}
          />
          <ExpandMore
            expand={expanded}
            onClick={handleExpandClick}
            aria-expanded={expanded}
            aria-label="show more"
            size="small"
          >
            <ExpandMoreIcon />
          </ExpandMore>
        </Box>

        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ mb: 2 }} />
            
            <Typography variant="subtitle2" gutterBottom>
              Assignments
            </Typography>
            
            <List dense sx={{ width: '100%', pt: 0 }}>
              {employee.assignments && employee.assignments.length > 0 ? (
                employee.assignments.map((assignment) => (
                  <ListItem key={assignment.id} sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <BusinessIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={assignment.projectName || 'No project name'} 
                      //secondary={`${assignment.departmentName || 'No department'} | ${assignment.roles?.map(r => r.label).join(', ') || 'No roles'}`}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))
              ) : (
                <ListItem sx={{ px: 0 }}>
                  <Typography variant="body2" color="text.secondary">
                    No assignments found
                  </Typography>
                </ListItem>
              )}
            </List>
            
            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
              Matching Templates
            </Typography>
            
            {renderTemplates()}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

EmployeeCard.propTypes = {
  employee: PropTypes.shape({
    employeeId: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    fullName: PropTypes.string.isRequired,
    position: PropTypes.string.isRequired,
    positionId: PropTypes.string.isRequired,
    assignmentsCount: PropTypes.number.isRequired,
    assignments: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        //departmentName: PropTypes.string.isRequired,
        projectName: PropTypes.string,
        roles: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            type: PropTypes.string.isRequired
          })
        )
      })
    ),
    hasMatchingTemplate: PropTypes.bool,
    matchingTemplates: PropTypes.array
  }).isRequired
};

export default EmployeeCard; 